import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Category } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface CategoriesState {
  categories: Category[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CategoriesState = {
  categories: [],
  isLoading: false,
  error: null,
};

export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.categories.list);
      return (response.data.data || response.data) as Category[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch categories'));
    }
  }
);

export const createCategory = createAsyncThunk(
  'categories/createCategory',
  async (data: Partial<Category>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.categories.create, data);
      return (response.data.data || response.data) as Category;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create category'));
    }
  }
);

export const updateCategory = createAsyncThunk(
  'categories/updateCategory',
  async ({ id, data }: { id: string; data: Partial<Category> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.categories.update(id), data);
      return (response.data.data || response.data) as Category;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update category'));
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'categories/deleteCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.categories.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete category'));
    }
  }
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState,
  reducers: {
    clearCategoryError: (state) => {
      state.error = null;
    },
    upsertCategory: (state, action: PayloadAction<Category>) => {
      const idx = state.categories.findIndex((c) => c.id === action.payload.id);
      if (idx >= 0) {
        state.categories[idx] = action.payload;
      } else {
        state.categories.push(action.payload);
      }
    },
    // [T68P1-c] Optimistic update actions for offline sync
    addOptimisticCategory: (state, action: PayloadAction<Category>) => {
      state.categories.unshift(action.payload);
    },
    replaceOptimisticCategory: (state, action: PayloadAction<{ optimisticId: string; realItem: Category }>) => {
      const idx = state.categories.findIndex(c => c.id === action.payload.optimisticId);
      if (idx !== -1) state.categories[idx] = action.payload.realItem;
    },
    removeOptimisticCategory: (state, action: PayloadAction<string>) => {
      state.categories = state.categories.filter(c => c.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.unshift(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        const idx = state.categories.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) {
          state.categories[idx] = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearCategoryError, upsertCategory,
  addOptimisticCategory, replaceOptimisticCategory, removeOptimisticCategory,
} = categoriesSlice.actions;
export default categoriesSlice.reducer;
